import React, { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  IconButton,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  EventAvailable as VacationIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, getDay, addMonths, subMonths } from 'date-fns';
import { pl } from 'date-fns/locale';
import { useMsal } from '@azure/msal-react';
import useAppStore from '../store/useAppStore';
import { scheduleAPI } from '../services/apiWithAuth';
import { useAuth } from '../hooks/useAuth';

const VacationCalendar = () => {
  const theme = useTheme();
  const { accounts } = useMsal();
  
  const {
    employees,
    workTypes,
    holidays,
    loadHolidays,
    isHoliday,
    getHoliday,
    setNotification
  } = useAppStore();

  // Hook autoryzacji do pobierania danych zalogowanego użytkownika
  const { user, isAuthenticated } = useAuth();
  
  const getCurrentUserEmail = () => {
    if (accounts && accounts.length > 0) {
      return accounts[0].username || 'user';
    }
    return 'user';
  };

  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [vacationMode, setVacationMode] = useState(true); // Tryb dodawania urlopów
  const [selectedVacationType, setSelectedVacationType] = useState('W'); // Domyślnie 'Wolne'
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [vacationData, setVacationData] = useState({});
  const [syncMode, setSyncMode] = useState(true); // Synchronizacja z głównym kalendarzem
  const [yearVacationStats, setYearVacationStats] = useState({}); // Statystyki urlopów dla całego roku

  // Filtruj tylko typy urlopowe - używamy useMemo żeby uniknąć nieskończonej pętli
  const vacationTypes = useMemo(() => 
    workTypes.filter(wt => 
      ['W', 'U', 'L4', 'DW', 'UZ', 'OW'].includes(wt.code)
    ), [workTypes]
  );

  // Ustaw domyślnego pracownika przy pierwszym załadowaniu
  useEffect(() => {
    // Ustaw domyślnego pracownika tylko jeśli nie ma już wybranego i są załadowani pracownicy
    if (!selectedEmployee && employees.length > 0) {
      let defaultEmployeeCode = null;

      // Dla środowiska produkcyjnego (z autoryzacją)
      if (isAuthenticated && user) {
        // Spróbuj dopasować zalogowanego użytkownika do pracownika
        // Możliwe mapowania: email -> employee code, username -> employee code
        const userEmail = user.email?.toLowerCase();
        const userName = user.name?.toLowerCase();
        
        // Szukaj pracownika po kodzie lub imieniu/nazwisku
        const matchedEmployee = employees.find(emp => {
          const empCode = emp.code?.toLowerCase();
          const empName = emp.name?.toLowerCase();
          const empEmail = emp.email?.toLowerCase();
          
          return empCode === userEmail || 
                 empName === userName || 
                 empEmail === userEmail ||
                 empCode === user.username?.toLowerCase();
        });

        if (matchedEmployee) {
          defaultEmployeeCode = matchedEmployee.code;
          console.log('✅ Ustawiono domyślnego pracownika z autoryzacji:', defaultEmployeeCode);
        }
      }

      // Dla środowiska dev (bez autoryzacji) - ustaw 'KS' jako domyślnego
      if (!defaultEmployeeCode) {
        const ksEmployee = employees.find(emp => emp.code === 'KS');
        if (ksEmployee) {
          defaultEmployeeCode = 'KS';
          console.log('🔧 DEV: Ustawiono KS jako domyślnego pracownika');
        }
      }

      // Jeśli znaleziono domyślnego pracownika, ustaw go
      if (defaultEmployeeCode) {
        setSelectedEmployee(defaultEmployeeCode);
      }
    }
  }, [employees, selectedEmployee, isAuthenticated, user]);


  // Generuj dni miesiąca
  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const monthDays = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Organizuj dni w tygodnie
  const weeks = [];
  let currentWeek = [];
  
  // Dodaj puste komórki na początku
  const firstDayOfWeek = getDay(monthStart);
  const startPadding = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Poniedziałek = 0
  
  for (let i = 0; i < startPadding; i++) {
    currentWeek.push(null);
  }
  
  monthDays.forEach(day => {
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
    currentWeek.push(day);
  });
  
  // Dodaj puste komórki na końcu
  while (currentWeek.length < 7) {
    currentWeek.push(null);
  }
  if (currentWeek.length > 0) {
    weeks.push(currentWeek);
  }

  // Ładowanie danych urlopowych
  const loadVacationData = async () => {
    if (!selectedEmployee) {
      setVacationData({});
      return;
    }

    try {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth() + 1;
      
      // Pobierz dane z głównego kalendarza dla wybranego pracownika
      const response = await scheduleAPI.getSchedule(year, month);
      console.log('🔍 VacationCalendar response:', response);
      
      // Filtruj tylko urlopy dla wybranego pracownika
      const employeeData = response.employees?.find(emp => emp.code === selectedEmployee);
      console.log('👤 Employee data:', employeeData);
      const vacations = {};
      
      if (employeeData && employeeData.schedule) {
        Object.entries(employeeData.schedule).forEach(([day, data]) => {
          // Sprawdź czy to typ urlopowy
          if (vacationTypes.some(vt => vt.code === data.work_type)) {
            vacations[parseInt(day)] = {
              type: data.work_type,
              text: data.custom_text || '',
              color: workTypes.find(wt => wt.code === data.work_type)?.color || '#gray'
            };
          }
        });
      }
      
      setVacationData(vacations);
    } catch (error) {
      console.error('Error loading vacation data:', error);
      setNotification({
        type: 'error',
        message: 'Błąd podczas ładowania danych urlopowych'
      });
    }
  };

  // Ładowanie statystyk urlopowych dla całego roku
  const loadYearVacationStats = async () => {
    if (!selectedEmployee) {
      setYearVacationStats({});
      return;
    }

    try {
      const year = currentMonth.getFullYear();
      const stats = {};
      
      // Pobierz dane ze wszystkich miesięcy roku
      for (let month = 1; month <= 12; month++) {
        const response = await scheduleAPI.getSchedule(year, month);
        const employeeData = response.employees?.find(emp => emp.code === selectedEmployee);
        
        if (employeeData && employeeData.schedule) {
          Object.entries(employeeData.schedule).forEach(([day, data]) => {
            // Sprawdź czy to typ urlopowy
            if (vacationTypes.some(vt => vt.code === data.work_type)) {
              stats[data.work_type] = (stats[data.work_type] || 0) + 1;
            }
          });
        }
      }
      
      setYearVacationStats(stats);
    } catch (error) {
      console.error('Error loading year vacation stats:', error);
    }
  };

  useEffect(() => {
    loadVacationData();
  }, [selectedEmployee, currentMonth, vacationTypes, workTypes, setNotification]);

  useEffect(() => {
    loadYearVacationStats();
  }, [selectedEmployee, currentMonth.getFullYear()]);

  // Load holidays when year changes
  useEffect(() => {
    const year = currentMonth.getFullYear();
    loadHolidays(year);
  }, [currentMonth.getFullYear(), loadHolidays]);

  // Nawigacja w miesiącach
  const navigateMonth = (direction) => {
    setCurrentMonth(prev => direction > 0 ? addMonths(prev, 1) : subMonths(prev, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  const handleDayClick = async (day) => {
    if (!day || !selectedEmployee) return;

    const year = day.getFullYear();
    const dayNumber = day.getDate();
    const isWeekend = [0, 6].includes(day.getDay());
    const month = day.getMonth() + 1;
    
    if (isWeekend) {
      setNotification({
        type: 'warning',
        message: 'Nie można dodawać urlopów w weekendy'
      });
      return;
    }

    // Check if day is a holiday
    if (isHoliday(year, month, dayNumber)) {
      const holiday = getHoliday(year, month, dayNumber);
      setNotification({
        type: 'warning',
        message: `Nie można dodawać urlopów w święta (${holiday?.name || 'Święto'})`
      });
      return;
    }

    try {
      const dayNum = day.getDate();

      const existingVacation = vacationData[dayNumber];
      
      if (existingVacation) {
        // Usuń istniejący urlop
        if (syncMode) {
          await scheduleAPI.deleteSchedule(selectedEmployee, year, month, dayNum, getCurrentUserEmail());
        }
        
        // Aktualizuj lokalne dane
        const newVacationData = { ...vacationData };
        delete newVacationData[dayNumber];
        setVacationData(newVacationData);
        
        setNotification({
          type: 'success',
          message: `Usunięto urlop z dnia ${dayNum}.${month}`
        });
        
        // Odśwież roczne statystyki
        loadYearVacationStats();
      } else {
        // Dodaj nowy urlop
        const scheduleEntry = {
          employee_code: selectedEmployee,
          year,
          month,
          day: dayNum,
          work_type_code: selectedVacationType,
          custom_text: '',
          updated_by: 'vacation_calendar'
        };

        if (syncMode) {
          await scheduleAPI.createSchedule(scheduleEntry);
        }
        
        // Aktualizuj lokalne dane
        const vacationType = workTypes.find(wt => wt.code === selectedVacationType);
        setVacationData(prev => ({
          ...prev,
          [dayNumber]: {
            type: selectedVacationType,
            text: '',
            color: vacationType?.color || '#gray'
          }
        }));
        
        setNotification({
          type: 'success',
          message: `Dodano urlop (${vacationType?.label}) na dzień ${dayNum}.${month}`
        });
        
        // Odśwież roczne statystyki
        loadYearVacationStats();
      }
    } catch (error) {
      console.error('Error updating vacation:', error);
      setNotification({
        type: 'error',
        message: 'Błąd podczas aktualizacji urlopu'
      });
    }
  };

  const getDayStyle = (day) => {
    if (!day) return {};
    
    const dayNumber = day.getDate();
    const year = day.getFullYear();
    const month = day.getMonth() + 1;
    const isToday = format(day, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');
    const isWeekend = getDay(day) === 0 || getDay(day) === 6;
    const hasVacation = vacationData[dayNumber];
    const isDayHoliday = isHoliday(year, month, dayNumber);
    const isDarkMode = theme.palette.mode === 'dark';

    let style = {
      width: 40,
      height: 40,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `2px solid ${isDarkMode ? '#4f46e5' : '#4f46e5'}`,
      cursor: selectedEmployee && vacationMode && !isWeekend && !isDayHoliday ? 'pointer' : 'default',
      borderRadius: '4px',
      position: 'relative',
      backgroundColor: 'transparent',
      color: isDarkMode ? 'white' : '#1e293b' // Ciemny tekst w trybie jasnym, biały w ciemnym
    };

    if (isDayHoliday) {
      // Holiday - strong purple with gradient-like effect
      style.backgroundColor = '#9c27b0';
      style.color = 'white';
      style.fontWeight = 'bold';
      style.border = '2px solid #7b1fa2';
      style.boxShadow = '0 2px 4px rgba(156, 39, 176, 0.3)';
      style.cursor = 'not-allowed';
    } else if (isWeekend) {
      style.backgroundColor = isDarkMode ? '#475569' : '#cbd5e1';
      style.color = isDarkMode ? '#94a3b8' : '#475569';
    } else if (hasVacation) {
      style.backgroundColor = '#fbbf24'; // Żółty dla dni z urlopem
      style.border = '2px solid #f59e0b';
      style.color = '#000';
      style.fontWeight = 'bold';
    } else if (isToday) {
      style.border = isDarkMode ? '2px solid white' : '2px solid #4f46e5';
      style.fontWeight = 'bold';
      style.color = '#4f46e5';
      style.boxShadow = isDarkMode 
        ? '0 0 8px rgba(255, 255, 255, 0.8), 0 0 4px rgba(99, 102, 241, 0.5)'
        : '0 0 8px rgba(79, 70, 229, 0.5), 0 0 4px rgba(99, 102, 241, 0.3)';
    }

    if (selectedEmployee && vacationMode && !isWeekend && !hasVacation && !isDayHoliday) {
      style.backgroundColor = isDarkMode ? 'rgba(79, 70, 229, 0.1)' : 'rgba(79, 70, 229, 0.05)';
      style.border = '2px solid #4f46e5';
    }

    return style;
  };

  const getVacationStats = () => {
    const stats = {};
    Object.values(vacationData).forEach(vacation => {
      stats[vacation.type] = (stats[vacation.type] || 0) + 1;
    });
    return stats;
  };

  const vacationStats = getVacationStats();

  return (
    <Box sx={{ p: 3 }}>
      {/* Kompaktowa górna sekcja - jedna linia */}
      <Card 
        elevation={0}
        sx={{ 
          mb: 3,
          background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.1) 100%)',
          border: '1px solid rgba(25, 118, 210, 0.2)',
          borderRadius: 2
        }}
      >
        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
          <Grid container spacing={3} alignItems="center">
            {/* Lewa część - kontrolki */}
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <VacationIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Kalendarz Urlopowy
                </Typography>
              </Box>
              
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Pracownik</InputLabel>
                    <Select
                      value={selectedEmployee}
                      onChange={(e) => setSelectedEmployee(e.target.value)}
                      label="Pracownik"
                    >
                      {employees.filter(emp => emp.is_active).map(employee => (
                        <MenuItem key={employee.code} value={employee.code}>
                          {employee.name} ({employee.code})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={3.5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Typ urlopu</InputLabel>
                    <Select
                      value={selectedVacationType}
                      onChange={(e) => setSelectedVacationType(e.target.value)}
                      label="Typ urlopu"
                    >
                      {vacationTypes.map(type => (
                        <MenuItem key={type.code} value={type.code}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box 
                              sx={{ 
                                width: 14, 
                                height: 14, 
                                backgroundColor: type.color, 
                                borderRadius: '50%' 
                              }} 
                            />
                            {type.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={5}>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={syncMode}
                          onChange={(e) => setSyncMode(e.target.checked)}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Synchronizacja</Typography>}
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={vacationMode}
                          onChange={(e) => setVacationMode(e.target.checked)}
                          size="small"
                        />
                      }
                      label={<Typography variant="body2">Edycja</Typography>}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>

            {/* Prawa część - kompaktowe podsumowanie */}
            <Grid item xs={12} md={4}>
              <Box 
                sx={{ 
                  p: 2,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(25, 118, 210, 0.2) 100%)',
                  border: '1px solid rgba(25, 118, 210, 0.3)',
                  textAlign: 'center',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontWeight: 500 }}>
                  {currentMonth.getFullYear()} rok
                </Typography>
                
                {selectedEmployee && Object.keys(yearVacationStats).length > 0 ? (
                  <>
                    <Typography variant="h3" color="primary" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {Object.values(yearVacationStats).reduce((a, b) => a + b, 0)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'block', fontWeight: 500 }}>
                      zarejestrowanych dni wolnych
                    </Typography>
                  </>
                ) : (
                  <Box sx={{ py: 1 }}>
                    <VacationIcon sx={{ fontSize: 36, color: 'text.disabled', opacity: 0.3, mb: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      {selectedEmployee ? 'Brak urlopów w tym roku' : 'Wybierz pracownika'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Nawigacja miesiąca */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3, gap: 2 }}>
        <IconButton onClick={() => navigateMonth(-1)} size="large">
          <ChevronLeftIcon fontSize="large" />
        </IconButton>
        
        <Typography variant="h4" sx={{ minWidth: 250, textAlign: 'center', fontWeight: 600 }}>
          {format(currentMonth, 'MMMM yyyy', { locale: pl })}
        </Typography>
        
        <IconButton onClick={() => navigateMonth(1)} size="large">
          <ChevronRightIcon fontSize="large" />
        </IconButton>
        
        <Button
          startIcon={<TodayIcon />}
          onClick={goToToday}
          variant="outlined"
        >
          Dziś
        </Button>
      </Box>

      {/* Kalendarz */}
      <Paper elevation={2}>
        <TableContainer>
          <Table size="medium">
            <TableHead>
              <TableRow>
                {['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela'].map(day => (
                  <TableCell key={day} align="center" sx={{ fontWeight: 'bold', py: 1.5, fontSize: '0.9rem' }}>
                    {day}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {weeks.map((week, weekIndex) => (
                <TableRow key={weekIndex}>
                  {week.map((day, dayIndex) => {
                    const dayNumber = day?.getDate();
                    const year = day?.getFullYear();
                    const month = day ? day.getMonth() + 1 : null;
                    const isDayHoliday = day ? isHoliday(year, month, dayNumber) : false;
                    const holiday = isDayHoliday ? getHoliday(year, month, dayNumber) : null;
                    
                    let tooltipTitle = '';
                    if (day) {
                      if (isDayHoliday) {
                        tooltipTitle = holiday?.name || 'Święto';
                      } else if (selectedEmployee && vacationMode) {
                        tooltipTitle = `Kliknij aby ${vacationData[dayNumber] ? 'usunąć' : 'dodać'} urlop`;
                      }
                    }
                    
                    return (
                      <TableCell key={dayIndex} align="center" sx={{ p: 0.75, border: '1px solid #bdbdbd', height: 65 }}>
                        {day && (
                          <Tooltip title={tooltipTitle}>
                            <Box
                              sx={{
                                ...getDayStyle(day),
                                width: 50,
                                height: 50,
                                fontSize: '1rem'
                              }}
                              onClick={() => handleDayClick(day)}
                            >
                              {dayNumber}
                              {vacationData[dayNumber] && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 3,
                                    right: 3,
                                    width: 7,
                                    height: 7,
                                    borderRadius: '50%',
                                    backgroundColor: 'white',
                                    border: '1px solid #9e9e9e'
                                  }}
                                />
                              )}
                            </Box>
                          </Tooltip>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Instrukcje */}
      {!selectedEmployee && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            Wybierz pracownika aby zobaczyć i edytować jego urlopy
          </Typography>
        </Box>
      )}
      
      {selectedEmployee && !vacationMode && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            Włącz "Tryb edycji" aby móc dodawać i usuwać urlopy
          </Typography>
        </Box>
      )}
      
      {selectedEmployee && vacationMode && (
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="textSecondary">
            Kliknij na dzień aby dodać urlop • Kliknij ponownie aby usunąć
            {!syncMode && (
              <Box component="span" sx={{ display: 'block', color: 'warning.main', mt: 1 }}>
                ⚠️ Synchronizacja wyłączona - zmiany nie będą zapisywane w głównym kalendarzu
              </Box>
            )}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default VacationCalendar;